import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, ActionCreator, Creator, Store } from '@ngrx/store';
import { concatLatestFrom } from '@ngrx/operators';
import { Observable, of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';
import {
  GetSessionSummaryResponse,
  PaymentInformationService,
  PaymentInformationServiceFactory,
} from '@arc/unified-payments-frontend/shared-payment';
import { selectVirtualOtherwisePrimaryTenantId } from '../session-store';

/**
 * Factory that creates a reusable NgRx functional effect reloading the (already-computed)
 * payment preview by payment reference for a receipt-landing page, once its own
 * session-summary load succeeds — feeds surcharge/discount visibility on the confirmation
 * screen and gates make-payment's selectInitialDataLoaded. `callService` lets each flow use
 * its own preview endpoint/response type (POI: getPaymentPreviewV1, POA:
 * getPayOnAccountPaymentPreviewV1 — different shapes, not interchangeable).
 *
 * Usage (POI, POA, international — each passes its own action set and service call):
 *
 *   export const poiReceiptLandingPaymentPreviewEffect =
 *     createReceiptLandingPaymentPreviewEffect({
 *       loadSessionSummarySuccessAction: PoiPaymentReceiptComponentActions.loadSessionSummarySuccess,
 *       loadPaymentPreviewSuccessAction: PoiPaymentReceiptComponentActions.loadPaymentPreviewSuccess,
 *       loadPaymentPreviewErrorAction: PoiPaymentReceiptComponentActions.loadPaymentPreviewError,
 *       callService: (service, tenantId, paymentReference) =>
 *         service.getPaymentPreviewV1(tenantId, paymentReference),
 *     });
 */
export function createReceiptLandingPaymentPreviewEffect<
  A extends Action<string> & { payload: GetSessionSummaryResponse },
  TResponse,
>(config: {
  loadSessionSummarySuccessAction: ActionCreator<string, Creator<any[], A>>;
  loadPaymentPreviewSuccessAction: ActionCreator<
    string,
    Creator<[{ payload: TResponse }], Action>
  >;
  loadPaymentPreviewErrorAction: ActionCreator<
    string,
    Creator<[{ error: HttpErrorResponse }], Action>
  >;
  callService: (
    service: PaymentInformationService,
    tenantId: string,
    paymentReference: string,
  ) => Observable<TResponse>;
}) {
  return createEffect(
    (
      actions$ = inject(Actions),
      store = inject(Store),
      paymentInformationServiceFactory = inject(
        PaymentInformationServiceFactory,
      ),
    ) =>
      actions$.pipe(
        ofType(config.loadSessionSummarySuccessAction),
        concatLatestFrom(() =>
          store.select(selectVirtualOtherwisePrimaryTenantId),
        ),
        exhaustMap(([action, tenantId]) =>
          config
            .callService(
              paymentInformationServiceFactory.create(),
              tenantId,
              action.payload.payment?.paymentReference,
            )
            .pipe(
              map(
                (response) =>
                  config.loadPaymentPreviewSuccessAction({
                    payload: response,
                  }) as Action,
              ),
              catchError((error: HttpErrorResponse) =>
                of(config.loadPaymentPreviewErrorAction({ error }) as Action),
              ),
            ),
        ),
      ),
    { functional: true },
  );
}
